// rsbuild substitutes env values only at `import.meta.env.KEY` reads. Spreading `import.meta.env`
// compiles to { MODE, DEV, PROD, BASE_URL, ASSET_PREFIX } without any PUBLIC_* value, which left
// the CustomWeather token empty and sent every radar tile through the backend proxy. Name each
// key here.
const ENV = Object.freeze({
  PUBLIC_DOPPLER_CW_TILE_BASE_URL: import.meta.env.PUBLIC_DOPPLER_CW_TILE_BASE_URL,
  PUBLIC_DOPPLER_CW_TILE_TOKEN: import.meta.env.PUBLIC_DOPPLER_CW_TILE_TOKEN,
  PUBLIC_DOPPLER_CW_PROXY_FALLBACK_ENABLED: import.meta.env
    .PUBLIC_DOPPLER_CW_PROXY_FALLBACK_ENABLED,
  PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_ENABLED: import.meta.env
    .PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_ENABLED,
  PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_TILE_ERRORS: import.meta.env
    .PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_TILE_ERRORS,
  PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_FRAME_COUNT: import.meta.env
    .PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_FRAME_COUNT,
  PUBLIC_DOPPLER_CURRENT_LAYER_TYPE: import.meta.env.PUBLIC_DOPPLER_CURRENT_LAYER_TYPE,
  PUBLIC_DOPPLER_FUTURE_LAYER_TYPE: import.meta.env.PUBLIC_DOPPLER_FUTURE_LAYER_TYPE,
  PUBLIC_DOPPLER_FUTURE_LAYER_START_MINUTES: import.meta.env
    .PUBLIC_DOPPLER_FUTURE_LAYER_START_MINUTES,
  PUBLIC_DOPPLER_DEFAULT_ZOOM: import.meta.env.PUBLIC_DOPPLER_DEFAULT_ZOOM,
  PUBLIC_DOPPLER_MIN_ZOOM: import.meta.env.PUBLIC_DOPPLER_MIN_ZOOM,
  PUBLIC_DOPPLER_MAX_ZOOM: import.meta.env.PUBLIC_DOPPLER_MAX_ZOOM,
  PUBLIC_DOPPLER_MAX_BOUNDS_VISCOSITY: import.meta.env.PUBLIC_DOPPLER_MAX_BOUNDS_VISCOSITY,
  PUBLIC_DOPPLER_DISABLE_WORLD_WRAP: import.meta.env.PUBLIC_DOPPLER_DISABLE_WORLD_WRAP,
  PUBLIC_DOPPLER_FORCE_HOME_ZOOM: import.meta.env.PUBLIC_DOPPLER_FORCE_HOME_ZOOM,
  PUBLIC_DOPPLER_TRAIL_OPACITY: import.meta.env.PUBLIC_DOPPLER_TRAIL_OPACITY,
  PUBLIC_DOPPLER_RETRY_DELAY_MS: import.meta.env.PUBLIC_DOPPLER_RETRY_DELAY_MS,
  PUBLIC_DOPPLER_VIEWPORT_SETTLE_DELAY_MS: import.meta.env.PUBLIC_DOPPLER_VIEWPORT_SETTLE_DELAY_MS,
  PUBLIC_DOPPLER_VIEWPORT_STABILIZE_DELAY_MS: import.meta.env
    .PUBLIC_DOPPLER_VIEWPORT_STABILIZE_DELAY_MS,
  PUBLIC_DOPPLER_FRAME_INTERVAL_MINUTES: import.meta.env.PUBLIC_DOPPLER_FRAME_INTERVAL_MINUTES,
  PUBLIC_DOPPLER_HISTORY_FRAME_COUNT: import.meta.env.PUBLIC_DOPPLER_HISTORY_FRAME_COUNT,
  PUBLIC_DOPPLER_FUTURE_FRAME_COUNT: import.meta.env.PUBLIC_DOPPLER_FUTURE_FRAME_COUNT,
});

const parseBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return fallback;
};

const parseNumber = (value, fallback) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const DOPPLER_PLAYBACK_SPEED_INTERVALS = Object.freeze([
  Object.freeze([0.25, 1600]),
  Object.freeze([0.5, 900]),
  Object.freeze([1, 420]),
  Object.freeze([1.5, 320]),
  Object.freeze([3, 220]),
  Object.freeze([5, 140]),
  Object.freeze([10, 80]),
]);

const DOPPLER_PLAYBACK_INTERVAL_BY_SPEED = Object.freeze(
  Object.fromEntries(DOPPLER_PLAYBACK_SPEED_INTERVALS),
);

export const DOPPLER_ROUTE = 'Doppler';
export const DOPPLER_ACCESSIBLE_NAME = 'Doppler radar';
export const DOPPLER_WIDGET_ACCESSIBLE_NAME = `${DOPPLER_ACCESSIBLE_NAME} widget`;
export const DOPPLER_PLAYBACK_SPEED_OPTIONS = Object.freeze(
  DOPPLER_PLAYBACK_SPEED_INTERVALS.map(([speed]) => speed),
);

export const DOPPLER_CONFIG = Object.freeze({
  // CustomWeather tile delivery
  customWeatherBaseUrl: ENV.PUBLIC_DOPPLER_CW_TILE_BASE_URL ?? '',
  customWeatherToken: ENV.PUBLIC_DOPPLER_CW_TILE_TOKEN ?? '',
  customWeatherProxyFallbackEnabled: parseBoolean(
    ENV.PUBLIC_DOPPLER_CW_PROXY_FALLBACK_ENABLED,
    true,
  ),
  directFailureFallbackEnabled: parseBoolean(
    ENV.PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_ENABLED,
    true,
  ),
  directFailureFallbackTileErrorThreshold: parseNumber(
    ENV.PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_TILE_ERRORS,
    6,
  ),
  directFailureFallbackFrameThreshold: parseNumber(
    ENV.PUBLIC_DOPPLER_DIRECT_FAILURE_FALLBACK_FRAME_COUNT,
    2,
  ),
  // Leave empty unless CustomWeather explicitly confirms a layer uses XYZ path segments.
  customWeatherXyzTypes: Object.freeze([]),
  currentRadarLayerType: ENV.PUBLIC_DOPPLER_CURRENT_LAYER_TYPE ?? 'future_radar',
  futureRadarLayerType: ENV.PUBLIC_DOPPLER_FUTURE_LAYER_TYPE ?? 'future_radar',
  futureRadarStartMinutes: parseNumber(ENV.PUBLIC_DOPPLER_FUTURE_LAYER_START_MINUTES, 10),

  // Map view
  defaultZoom: parseNumber(ENV.PUBLIC_DOPPLER_DEFAULT_ZOOM, 7),
  minZoom: parseNumber(ENV.PUBLIC_DOPPLER_MIN_ZOOM, 4),
  maxZoom: parseNumber(ENV.PUBLIC_DOPPLER_MAX_ZOOM, 18),
  maxBounds: Object.freeze([
    [20, -130],
    [55, -60],
  ]),
  maxBoundsViscosity: parseNumber(ENV.PUBLIC_DOPPLER_MAX_BOUNDS_VISCOSITY, 1),
  disableWorldWrap: parseBoolean(ENV.PUBLIC_DOPPLER_DISABLE_WORLD_WRAP, true),

  // When true, fit/zoom the map to the user's preferred place viewport on load
  // instead of using defaultZoom. Off by default to reduce unneeded tile calls.
  forceHomeZoom: parseBoolean(ENV.PUBLIC_DOPPLER_FORCE_HOME_ZOOM, false),

  // Radar tile bounds - tiles are only requested/rendered within this box.
  // Contiguous US (approx): SW below FL/west of WA, NE above WA/MN, east of ME.
  radarBounds: Object.freeze([
    [24.396308, -124.848974],
    [49.384358, -67.93457],
  ]),

  // Leaflet pane / layer stacking
  paneName: 'doppler-radar-pane',
  baseZIndex: 350,
  trailZIndex: 351,
  currentZIndex: 352,
  // The previous collection's visible frame stays above the new collection while the new one loads.
  retiringZIndex: 353,
  trailOpacity: parseNumber(ENV.PUBLIC_DOPPLER_TRAIL_OPACITY, 0),

  // Timing (ms)
  retryDelayMs: parseNumber(ENV.PUBLIC_DOPPLER_RETRY_DELAY_MS, 1500),
  // Longest time the previous radar image stays up when the new frames do not load.
  retiringLayerTimeoutMs: 20000,
  viewportSettleDelayMs: parseNumber(ENV.PUBLIC_DOPPLER_VIEWPORT_SETTLE_DELAY_MS, 1400),
  viewportStabilizeDelayMs: parseNumber(ENV.PUBLIC_DOPPLER_VIEWPORT_STABILIZE_DELAY_MS, 1200),

  // Frame generation
  frameIntervalMinutes: parseNumber(ENV.PUBLIC_DOPPLER_FRAME_INTERVAL_MINUTES, 10),
  historyFrameCount: parseNumber(ENV.PUBLIC_DOPPLER_HISTORY_FRAME_COUNT, 0),
  futureFrameCount: parseNumber(ENV.PUBLIC_DOPPLER_FUTURE_FRAME_COUNT, 11),

  // Playback speed mapping (ms between frame advances per speed multiplier)
  playbackIntervalBySpeed: DOPPLER_PLAYBACK_INTERVAL_BY_SPEED,
  basePlaybackIntervalMs: 420,
  minPlaybackIntervalMs: 80,
  maxPlaybackIntervalMs: 2200,

  // UI
  precipitationGradient:
    'linear-gradient(270deg, #630024 0%, #f15404 33%, #dae600 66%, #0fb344 100%)',
});

export const DOPPLER_RADAR_LAYER_OPTIONS = Object.freeze({
  pane: DOPPLER_CONFIG.paneName,
  bounds: DOPPLER_CONFIG.radarBounds,
  noWrap: true,
  updateWhenIdle: true,
  updateWhenZooming: false,
  updateInterval: 250,
  // Radar layers are stacked about 12 deep. A large off-screen tile ring
  // multiplies the tile count by every frame, so keep only a small buffer.
  keepBuffer: 1,
  opacity: 0,
  zIndex: DOPPLER_CONFIG.baseZIndex,
});
